import { useState, useEffect, useCallback, useContext } from "react";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Container,
  Stack,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { id } from "date-fns/locale";
import { format } from "date-fns";
import { axiosPrivate } from "../../../common/axiosPrivate";
import AuthContext from "../../../context/authContext";
import FreeTrackingMap from '../../../components/tracking-map/FreeTrackingMap';

export default function TrackingPenagihanPage() {
  const { user } = useContext(AuthContext);
  const [date, setDate] = useState(null);
  const [petugas, setPetugas] = useState("");
  const [petugasList, setPetugasList] = useState([]);
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPetugasList = async () => {
      try {
        let response;
        if ((user?.role === "area" || user?.role === "mis") && user?.kantorList?.length > 0) {
          response = await axiosPrivate.get("/pegawai/dropdown/list/all", {
            params: {
              role: user.role,
              kantorList: user.kantorList.join(","),
            },
          });
        } else if (user?.role === "pusat") {
          response = await axiosPrivate.get("/pegawai/dropdown/list/all", {
            params: { role: user.role },
          });
        } else if (user?.kantor) {
          response = await axiosPrivate.get(`/pegawai/dropdown/list/${user.kantor}`);
        } else {
          response = await axiosPrivate.get("/pegawai/dropdown/list/all");
        }
        setPetugasList(response.data || []);
      } catch (error) {
        console.error("Error fetching petugas list:", error);
      }
    };
    fetchPetugasList();
  }, [user]);

  const fetchTrackingData = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const response = await axiosPrivate.post("/penagihan/get-by-petugas", {
        date: format(date, "yyyy-MM-dd"),
        petugas,
        role: user?.role,
        kantorList: user?.kantorList,
      });
      const sortedData = response.data.length
        ? [...response.data]
            .filter(
              (item) =>
                item.koordinat &&
                item.koordinat.lat != null &&
                item.koordinat.lng != null
            )
            .sort(
              (a, b) =>
                new Date(a.tanggal).getTime() - new Date(b.tanggal).getTime()
            )
        : [];

      setTrackingData(sortedData);
    } catch (error) {
      console.error("Error fetching tracking data:", error);
    } finally {
      setLoading(false);
    }
  }, [date, petugas, user?.role, user?.kantorList]);

  return (
    <>
      <Helmet>
        <title>Tracking Maps | SIP App</title>
      </Helmet>

      <Container>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Tracking Maps by Petugas
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Tanggal"
              value={date}
              onChange={setDate}
              slotProps={{ textField: { size: "small", sx: { height: 40 } } }}
            />
          </LocalizationProvider>

          <Autocomplete
            options={petugasList}
            getOptionLabel={(option) => option.nama}
            value={petugasList.find((p) => p.id === petugas) || null}
            onChange={(_, newValue) => setPetugas(newValue ? newValue.id : "")}
            sx={{ width: 240, height: 40 }}
            renderInput={(params) => (
              <TextField {...params} label="Petugas" size="small" />
            )}
          />

          <Button
            variant="contained"
            onClick={fetchTrackingData}
            disabled={loading}
            sx={{ height: 40, minWidth: 100 }}
          >
            Submit
          </Button>
        </Stack>

        <FreeTrackingMap points={trackingData} />
      </Container>

    </>
  );
}
